import { Select } from '@mantine/core'
import { sortSections } from 'App/Map/LayerNav/Layers/generateNavigationMenu/generateNavigationMenu'
import { FireBlightSecondaryDropdown } from 'App/Map/MapControls/ValueRow/FireBlightSecondaryDropdown/FireBlightSecondaryDropdown'
import { WindMachineSecondaryDropdown } from 'App/Map/MapControls/ValueRow/WindMachineSecondaryDropdown/WindMachineSecondaryDropdown'
import { translate } from 'i18n/i18n'
import { cloneDeep } from 'lodash'
import { useMemo } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { defaultMapVisualValueGroup } from 'stores/mapControlsStore/mapVisualValueGroup'
import type { MAP_VISUAL } from 'stores/mapControlsStore/types'
import {
  degreeDaysRecord,
  larvalTrendRecord,
  phenologyRecord,
  trapCatchesRecord,
} from 'stores/selectedValueGroupsStore/insectValueGroupRecords'
import type { TValueGroup } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import {
  selectedValueGroupsStore,
  valueGroupToSectionDictionary,
} from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import { getInsectNameFromId } from 'utils/getInsectNameFromId'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'
import { useIsServiceCenterActive } from 'utils/useIsServiceCenterActive'
import { usePrimaryValueGroup } from 'utils/usePrimaryValueGroup'
import { AlmondBloomSecondaryDropdown } from './AlmondBloomSecondaryDropdown/AlmondBloomSecondaryDropdown'
import { AlternariaSecondaryDropdown } from './AlternariaSecondaryDropdown/AlternariaSecondaryDropdown'
import { BeeHoursSecondaryDropdown } from './BeeHoursSecondaryDropdown/BeeHoursSecondaryDropdown'
import { ChillSecondaryDropdown } from './ChillSecondaryDropdown/ChillSecondaryDropdown'
import { EvapotranspirationSecondaryDropdown } from './EvapotranspirationSecondaryDropdown/EvapotranspirationSecondaryDropdown'
import { FruitGrowthSecondaryDropdown } from './FruitGrowthSecondaryDropdown/FruitGrowthSecondaryDropdown'
import { InsectDegreeDaysSecondaryDropdown } from './InsectDegreeDaysSecondaryDropdown/InsectDegreeDaysSecondaryDropdown'
import { InsectTrapCatchesSecondaryDropdown } from './InsectTrapCatchesSecondaryDropdown/InsectTrapCatchesSecondaryDropdown'
import { IrrigationActivitySecondaryDropdown } from './IrrigationActivitySecondaryDropdown/IrrigationActivitySecondaryDropdown'
import { IrrigationActivityZonesSecondaryDropdown } from './IrrigationActivityZonesSecondaryDropdown/IrrigationActivityZonesSecondaryDropdown'
import { IrrigationFlowMeterSecondaryDropdown } from './IrrigationFlowMeterSecondaryDropdown/IrrigationFlowMeterSecondaryDropdown'
import { LeafWetnessSecondaryDropdown } from './LeafWetnessSecondaryDropdown/LeafWetnessSecondaryDropdown'
import { PrecipitationSecondaryDropdown } from './PrecipitationSecondaryDropdown/PrecipitationSecondaryDropdown'
import { SoilSecondaryDropdown } from './SoilSecondaryDropdown/SoilSecondaryDropdown'
import { TemperatureSecondaryDropdown } from './TemperatureSecondaryDropdown/TemperatureSecondaryDropdown'

const SecondaryChild: Record<TValueGroup, React.ReactNode> = {
  air_temperature: <TemperatureSecondaryDropdown />,
  almond_bloom: <AlmondBloomSecondaryDropdown />,
  almond_hull_split: null,
  alternaria: <AlternariaSecondaryDropdown />,
  atmospheric_pressure: null,
  bee_hours: <BeeHoursSecondaryDropdown />,
  cherry_powdery_mildew: null,
  chill: <ChillSecondaryDropdown />,
  conditions: null,
  wind_machine: <WindMachineSecondaryDropdown />,
  ...degreeDaysRecord(() => <InsectDegreeDaysSecondaryDropdown />),
  dew_point: null,
  equipment_status: null,
  evapotranspiration: <EvapotranspirationSecondaryDropdown />,
  fire_blight: <FireBlightSecondaryDropdown />,
  fruit_growth: <FruitGrowthSecondaryDropdown />,
  grape_powdery_mildew: null,
  plant_stress: null,
  humidity: null,
  irrigation_activity_pressure: <IrrigationActivitySecondaryDropdown />,
  irrigation_activity_zones: <IrrigationActivityZonesSecondaryDropdown />,
  irrigation_activity_flow_meters: <IrrigationFlowMeterSecondaryDropdown />,
  ...larvalTrendRecord(() => null),
  leaf_wetness: <LeafWetnessSecondaryDropdown />,
  ndvi: null,
  precipitation: <PrecipitationSecondaryDropdown />,
  soil: <SoilSecondaryDropdown />,
  spray_conditions: null,
  ...trapCatchesRecord(() => <InsectTrapCatchesSecondaryDropdown />),
  ...phenologyRecord(() => null),
  wet_bulb: null,
  wind: null,
}

export const ValueRow = () => {
  const primaryValueGroup = usePrimaryValueGroup()
  const selectedValueGroups = selectedValueGroupsStore.useSelector((s) => s.selectedValueGroups)
  const isPropertyPlaybackActive = smallStore.useSelector((s) => s.showPropertyPlayback)
  const isServiceCenterActive = useIsServiceCenterActive()

  const data = useMemo(() => {
    return Object.entries(selectedValueGroups)
      .flatMap(([valueGroup, active]) => {
        const label = (() => {
          if (valueGroup.includes('degree_days_insect_id_'))
            return translate.phrases.banyanApp('{{insectName}} Degree Days', {
              insectName: getInsectNameFromId(Number(valueGroup.replace('degree_days_insect_id_', ''))),
            })

          // TOD0: hack until we have regional trap stuff for the map
          if (valueGroup.includes('trap_catches_insect_id_') && !isUserOnlyAFreeRegionalUser())
            return translate.phrases.banyanApp('{{insectName}} Trap Catches', {
              insectName: getInsectNameFromId(Number(valueGroup.replace('trap_catches_insect_id_', ''))),
            })

          if (valueGroup === 'air_temperature') return translate.phrases.banyanApp('Temperature')

          if (valueGroup === 'almond_bloom') return translate.phrases.banyanApp('Almond Bloom')

          if (valueGroup === 'almond_hull_split')
            return translate.phrases.templates('{{labelA}} ({{labelB}})', {
              labelA: translate.phrases.banyanApp('Almond Hull Split'),
              labelB: translate.phrases.banyanApp('Nonpareil'),
            })

          if (valueGroup === 'alternaria') return translate.phrases.banyanApp('Alternaria')

          if (valueGroup === 'bee_hours') return translate.phrases.banyanApp('Bee Hours')

          if (valueGroup === 'cherry_powdery_mildew')
            return translate.phrases.banyanApp('Cherry Powdery Mildew')

          if (valueGroup === 'chill') return translate.phrases.banyanApp('Chill')

          if (valueGroup === 'dew_point') return translate.phrases.banyanApp('Dew Point')

          if (valueGroup === 'evapotranspiration') return translate.phrases.banyanApp('Evapotranspiration')

          if (valueGroup === 'fire_blight') return translate.phrases.banyanApp('Fire Blight')

          if (valueGroup === 'fruit_growth') return translate.phrases.banyanApp('Fruit Growth')

          if (valueGroup === 'grape_powdery_mildew')
            return translate.phrases.banyanApp('Grape Powdery Mildew')

          if (valueGroup === 'plant_stress') return translate.phrases.banyanApp('Plant Stress')

          if (valueGroup === 'wet_bulb') return translate.phrases.banyanApp('Wet Bulb')

          if (valueGroup === 'humidity') return translate.phrases.banyanApp('Humidity')

          if (valueGroup === 'equipment_status') return translate.phrases.banyanApp('Equipment Status')

          // TODO: conditions

          if (valueGroup === 'precipitation') return translate.phrases.banyanApp('Rain')

          if (valueGroup === 'soil') return translate.phrases.banyanApp('Soil')

          if (valueGroup === 'spray_conditions') return translate.phrases.banyanApp('Spray Conditions')

          if (valueGroup === 'wind') return translate.phrases.banyanApp('Wind')

          if (valueGroup === 'wind_machine') return translate.phrases.banyanApp('Wind Machine')

          if (valueGroup === 'irrigation_activity_pressure')
            return translate.phrases.templates('{{labelA}} - {{labelB}}', {
              labelA: translate.phrases.banyanApp('Irrigation Activity'),
              labelB: translate.phrases.banyanApp('Pressure'),
            })

          if (valueGroup === 'irrigation_activity_zones')
            return translate.phrases.templates('{{labelA}} - {{labelB}}', {
              labelA: translate.phrases.banyanApp('Irrigation Activity'),
              labelB: translate.phrases.banyanApp('Zones'),
            })

          if (valueGroup === 'irrigation_activity_flow_meters')
            return translate.phrases.templates('{{labelA}} - {{labelB}}', {
              labelA: translate.phrases.banyanApp('Irrigation Activity'),
              labelB: translate.phrases.banyanApp('Flow Meters'),
            })

          if (valueGroup === 'leaf_wetness') return translate.phrases.banyanApp('Leaf Wetness')

          // TODO: if it's not here, we haven't implemented  it yet. This will clean up the UI along with our filter later
          return ''
        })()

        const section = valueGroupToSectionDictionary[valueGroup as TValueGroup]

        const sectionLabel = (() => {
          if (section === 'environment') return translate.phrases.banyanApp('Environment')

          if (section === 'water-management') return translate.phrases.banyanApp('Water Management')

          if (section === 'crop-development') return translate.phrases.banyanApp('Crop Development')

          if (section === 'sprays') return translate.phrases.banyanApp('Sprays')

          if (section === 'insects') return translate.phrases.banyanApp('Pests')

          if (section === 'equipment') return translate.phrases.banyanApp('Equipment')

          if (section === 'diseases') return translate.phrases.banyanApp('Diseases')
        })()

        if (!active || !label) return []

        return {
          section,
          group: sectionLabel,
          value: valueGroup,
          label,
        }
      })
      .sort((a, b) => sortSections(a.section, b.section) || alphabeticalSort(a.label, b.label))
      .reduce<
        ({ group: string; items: { value: string; label: string }[] } | { value: string; label: string })[]
      >((groupedData, item) => {
        if (!item.group) {
          groupedData.push({ value: item.value, label: item.label })

          return groupedData
        }

        const lastGroup = groupedData[groupedData.length - 1]

        if (lastGroup && 'group' in lastGroup && lastGroup.group === item.group) {
          lastGroup.items.push({ value: item.value, label: item.label })
        } else {
          groupedData.push({ group: item.group, items: [{ value: item.value, label: item.label }] })
        }

        return groupedData
      }, [])
  }, [primaryValueGroup, selectedValueGroups])

  if (!data.length) return null

  // Currently, SSC has only Equipment Status select option so we don't need to show the select
  if (isServiceCenterActive) return null

  return (
    <>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        }}
      >
        <Select
          // Property playback is only for temperature for now
          disabled={isPropertyPlaybackActive}
          data={data}
          onChange={(value) => {
            if (value === null) return

            const newValueGroup = value as TValueGroup

            mapControlsStore.setState((s) => {
              const newMapVisualsValueGroup = cloneDeep(s.mapVisualValueGroup)

              Object.entries(newMapVisualsValueGroup).forEach(([mapVisual, valueGroupForMapVisual]) => {
                if (!valueGroupForMapVisual[newValueGroup]) {
                  valueGroupForMapVisual[newValueGroup] =
                    defaultMapVisualValueGroup[mapVisual as MAP_VISUAL]?.[newValueGroup]
                }
              })

              return {
                ...s,
                mapVisualValueGroup: newMapVisualsValueGroup,
              }
            })

            selectedValueGroupsStore.setState((s) => {
              return { ...s, primaryValueGroup: newValueGroup }
            })
          }}
          css={{ width: '100%' }}
          styles={{ ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER }}
          value={primaryValueGroup}
          comboboxProps={{ withinPortal: true }}
        />
      </div>
      {!!SecondaryChild[primaryValueGroup as TValueGroup] && (
        <div css={{ marginTop: 10 }}>{SecondaryChild[primaryValueGroup as TValueGroup]}</div>
      )}
    </>
  )
}
